import React, { useState, useRef } from "react";
import { 
  Edit3, 
  Camera, 
  Save, 
  X, 
  Lock,
  Mail,
  Trash2,
  LogOut,
  Calendar,
  Zap
} from "lucide-react";
import { useAuthStore } from "../store/authStore";
import { useAuth } from "../contexts/AuthContext";
import { userService } from "../services/userService";
import { Button, Input, LoadingSpinner, ProfileAvatar } from "../components/ui";
import { toast } from "../components/ui/Toaster";
import type { UpdateProfileRequest } from "../types";
import ChangePasswordModal from "../components/modals/ChangePasswordModal";

interface ProfileFormData {
  username: string;
  email: string;
  age?: number | undefined;
  gender?: string;
  bio?: string;
  drugUsePreference?: string;
  socialPreference?: string;
}

// Database to enum value mappings
const dbToEnumMapping = {
  gender: {
    'man': 'MAN',
    'vrouw': 'VROUW',
    'transgender': 'TRANSGENDER',
    'non_binair': 'NON_BINAIR',
    'genderfluide': 'GENDERFLUIDE',
    'anders': 'ANDERS',
    'zeg_ik_liever_niet': 'LIEVER_NIET'
  },
  socialPreference: {
    'introvert': 'INTROVERT',
    'verlegen_niet_introvert': 'VERLEGEN_NIET_INTROVERT',
    'introvert_nuchter': 'INTROVERT_NUCHTER',
    'gemiddeld': 'GEMIDDELD',
    'beetje_extrovert': 'BEETJE_EXTROVERT',
    'super_extrovert': 'SUPER_EXTROVERT'
  },
  drugUsePreference: {
    'volledig_nuchter_iedereen': 'VOLLEDIG_NUCHTER_IEDEREEN',
    'volledig_nuchter_anderen': 'VOLLEDIG_NUCHTER_ANDEREN',
    'alcohol_met_anderen': 'ALCOHOL_MET_ANDEREN',
    'alcohol_anderen_vrij': 'ALCOHOL_ANDEREN_VRIJ',
    'ga_hard_anderen_vrij': 'GA_HARD_ANDEREN_VRIJ',
    'ga_hard_met_anderen': 'GA_HARD_MET_ANDEREN',
    'flexibel_omgeving': 'FLEXIBEL_OMGEVING'
  }
};


const ProfilePage: React.FC = () => {
  const { user, setUser } = useAuthStore();
  const { logout } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
  

  const [formData, setFormData] = useState<ProfileFormData>({
    username: user?.username || '',
    email: user?.email || '',
    age: user?.age || undefined,
    gender: user?.gender || '', // API returns enum values directly
    bio: user?.bio || '',
    drugUsePreference: user?.drugUsePreference || '', // API returns enum values directly
    socialPreference: user?.socialPreference || '', // API returns enum values directly
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Use enum values (uppercase) to match backend validation
  const genderOptions = [
    { value: 'MAN', label: 'Man' },
    { value: 'VROUW', label: 'Vrouw' },
    { value: 'TRANSGENDER', label: 'Transgender' },
    { value: 'NON_BINAIR', label: 'Non-binair' },
    { value: 'GENDERFLUIDE', label: 'Genderfluide' },
    { value: 'ANDERS', label: 'Anders (leg uit in bio)' },
    { value: 'LIEVER_NIET', label: 'Zeg ik liever niet' },
  ];

  const drugUsePreferenceOptions = [
    { value: 'VOLLEDIG_NUCHTER_IEDEREEN', label: 'Volledig nuchter, liefst iedereen om mij heen ook' },
    { value: 'VOLLEDIG_NUCHTER_ANDEREN', label: 'Volledig nuchter, anderen mogen doen wat ze willen' },
    { value: 'ALCOHOL_MET_ANDEREN', label: 'Ik drink alcohol, liefst met anderen' },
    { value: 'ALCOHOL_ANDEREN_VRIJ', label: 'Ik drink alcohol, anderen mogen doen wat ze willen' },
    { value: 'GA_HARD_ANDEREN_VRIJ', label: 'Ik ga hard, anderen mogen doen wat ze willen' },
    { value: 'GA_HARD_MET_ANDEREN', label: 'Ik ga hard, liefst met anderen' },
    { value: 'FLEXIBEL_OMGEVING', label: 'Ik ben flexibel, pas mij aan aan mijn omgeving' },
  ];

  const socialPreferenceOptions = [
    { value: 'ERG_INTROVERT', label: 'Erg introvert' },
    { value: 'BEETJE_INTROVERT', label: 'Een beetje introvert' },
    { value: 'INTROVERT_NUCHTER', label: 'Alleen introvert als ik nuchter ben' },
    { value: 'GEMIDDELD', label: 'Gemiddeld' },
    { value: 'VERLEGEN_NIET_INTROVERT', label: 'Verlegen maar niet introvert' },
    { value: 'BEETJE_EXTROVERT', label: 'Een beetje extrovert' },
    { value: 'EXTROVERT_VERLEGEN', label: 'Extrovert maar verlegen' },
    { value: 'EXTROVERT', label: 'Extrovert' },
    { value: 'SUPER_EXTROVERT', label: 'Echt super extrovert' },
  ];

  const handleEditToggle = () => {
    if (isEditing) {
      // Reset form data when canceling (API returns enum values directly)
      setFormData({
        username: user?.username || '',
        email: user?.email || '',
        age: user?.age || undefined,
        gender: user?.gender || '', // API returns enum values directly
        bio: user?.bio || '',
        drugUsePreference: user?.drugUsePreference || '', // API returns enum value directly
        socialPreference: user?.socialPreference || '', // API returns enum values directly
      });
      setErrors({});
    }
    setIsEditing(!isEditing);
  };

  const handleInputChange = (field: keyof ProfileFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear field error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.username.trim()) {
      newErrors.username = 'Gebruikersnaam is verplicht';
    } else if (formData.username.trim().length < 3) {
      newErrors.username = 'Gebruikersnaam moet minimaal 3 tekens zijn';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'E-mailadres is verplicht';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Ongeldig e-mailadres';
    }

    if (formData.age !== undefined && (formData.age < 16 || formData.age > 100)) {
      newErrors.age = 'Leeftijd moet tussen 16 en 100 zijn';
    }

    if (formData.bio && formData.bio.length > 500) {
      newErrors.bio = 'Bio mag niet meer dan 500 tekens bevatten';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setIsLoading(true);
      
      // Build update data, only including fields that have values
      const updateData: UpdateProfileRequest = {};
      
      // Only add fields if they have actual values (not empty strings)
      if (formData.age !== undefined && formData.age !== null) {
        updateData.age = formData.age;
      }
      if (formData.gender && formData.gender.trim() !== '') {
        updateData.gender = formData.gender;
      }
      if (formData.bio !== undefined && formData.bio !== null) {
        updateData.bio = formData.bio;
      }
      if (formData.drugUsePreference && formData.drugUsePreference.trim() !== '') {
        updateData.drugUsePreference = formData.drugUsePreference;
      }
      if (formData.socialPreference && formData.socialPreference.trim() !== '') {
        updateData.socialPreference = formData.socialPreference;
      }
      
      // Check if there's anything to update
      if (Object.keys(updateData).length === 0) {
        toast.info('Geen wijzigingen', 'Er zijn geen wijzigingen om op te slaan');
        setIsEditing(false);
        return;
      }

      const updatedUser = await userService.updateProfile(updateData);
      
      setUser(updatedUser);
      
      toast.success('Gelukt!', 'Profiel succesvol bijgewerkt');
      setIsEditing(false);
    } catch (error: any) {
      toast.error('Fout', error.message || 'Kon profiel niet bijwerken');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Fout', 'Afbeelding moet kleiner zijn dan 2MB');
      return;
    }

    // Validate file type
    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      toast.error('Fout', 'Alleen JPG en PNG afbeeldingen zijn toegestaan');
      return;
    }

    try {
      setIsUploadingPhoto(true);
      const updatedUser = await userService.uploadProfilePhoto(file);
      setUser(updatedUser);
      toast.success('Gelukt!', 'Profielfoto bijgewerkt');
    } catch (error: any) {
      console.error('Failed to upload photo:', error);
      toast.error('Fout', error.message || 'Kon foto niet uploaden');
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const handleDeletePhoto = async () => {
    try {
      const updatedUser = await userService.deleteProfilePhoto();
      setUser(updatedUser);
      toast.success('Gelukt!', 'Profielfoto verwijderd');
    } catch (error: any) {
      console.error('Failed to delete photo:', error);
      toast.error('Fout', error.message || 'Kon foto niet verwijderen');
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Gelukt!', 'Je bent uitgelogd');
    } catch (error: any) {
      console.error('Logout failed:', error);
      toast.error('Fout', 'Uitloggen mislukt');
    }
  };

  const formatGender = (gender: string) => {
    // First try direct enum match, then try DB value conversion
    let option = genderOptions.find(opt => opt.value === gender);
    if (!option) {
      const enumValue = dbToEnumMapping.gender[gender as keyof typeof dbToEnumMapping.gender];
      option = genderOptions.find(opt => opt.value === enumValue);
    }
    return option?.label || gender;
  };

  const formatDrugUsePreference = (preference: string) => {
    if (!preference) return 'Niet opgegeven';
    // First try direct enum match, then try DB value conversion
    let option = drugUsePreferenceOptions.find(opt => opt.value === preference);
    if (!option) {
      const enumValue = dbToEnumMapping.drugUsePreference[preference as keyof typeof dbToEnumMapping.drugUsePreference];
      option = drugUsePreferenceOptions.find(opt => opt.value === enumValue);
    }
    return option?.label || preference;
  };

  const formatSocialPreference = (preference: string) => {
    // First try direct enum match, then try DB value conversion
    let option = socialPreferenceOptions.find(opt => opt.value === preference);
    if (!option) {
      const enumValue = dbToEnumMapping.socialPreference[preference as keyof typeof dbToEnumMapping.socialPreference];
      option = socialPreferenceOptions.find(opt => opt.value === enumValue);
    }
    return option?.label || 'Niet opgegeven';
  };

  return (
    <div className="mx-auto max-w-[920px] space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-5">
        <div>
          <div className="sf-eyebrow mb-3">// profiel</div>
          <h1 className="sf-h1">Profiel</h1>
          <p className="mt-2.5 max-w-[460px] text-[15px] text-fg-muted">
            Vul je profiel volledig in en plaats een foto — dat verhoogt de kans dat je in groepen wordt toegelaten!
          </p>
        </div>
        <div className="flex flex-col space-y-3 sm:flex-row sm:space-y-0 sm:space-x-3">
          {isEditing ? (
            <>
              <Button
                type="button"
                variant="secondary"
                onClick={handleEditToggle}
                disabled={isLoading}
                size="md"
              >
                <X className="h-4 w-4 mr-2" />
                Annuleren
              </Button>
              <Button
                type="button"
                onClick={handleSubmit}
                loading={isLoading}
                size="md"
              >
                <Save className="h-4 w-4 mr-2" />
                Wijzigingen opslaan
              </Button>
            </>
          ) : (
            <Button
              type="button"
              variant="secondary"
              onClick={handleEditToggle}
              size="md"
            >
              <Edit3 className="h-4 w-4 mr-2" />
              Profiel bewerken
            </Button>
          )}
        </div>
      </div>

      <div className="space-y-6">
        {/* Main Profile */}
        <div className="sf-card p-5 sm:p-7">
            <h2 className="mb-6 text-xl font-bold text-fg-bright">Profielinformatie</h2>
            {/* Profile Information Section */}
            <div className="mb-7">
              <div className="flex flex-col space-y-6 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-6">
                {/* Profile Photo */}
                <div className="flex-shrink-0 relative self-center sm:self-start">
                  <div
                    className={`${isEditing ? 'cursor-pointer' : ''}`}
                    onClick={isEditing ? () => fileInputRef.current?.click() : undefined}
                  >
                    <ProfileAvatar
                      user={user}
                      size="xl"
                      className="object-cover shadow-avatar-glow"
                    />
                  </div>

                  {isEditing && (
                    <>
                      <div className="absolute -bottom-2 -right-2">
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          disabled={isUploadingPhoto}
                          className="p-2 bg-neon-gradient text-ink rounded-full shadow-neon-pink hover:opacity-90 transition-opacity"
                        >
                          {isUploadingPhoto ? (
                            <LoadingSpinner size="sm" />
                          ) : (
                            <Camera className="h-4 w-4" />
                          )}
                        </button>
                      </div>

                      {user?.profilePhoto && (
                        <div className="absolute -bottom-2 -left-2">
                          <button
                            onClick={handleDeletePhoto}
                            className="p-2 bg-neon-pink/[.15] border border-neon-pink/40 text-neon-pink-soft rounded-full hover:bg-neon-pink/25 transition-colors"
                            title="Profielfoto verwijderen"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      )}

                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png"
                        onChange={handlePhotoUpload}
                        className="hidden"
                      />
                    </>
                  )}
                </div>

                {/* Basic Info Form */}
                <div className="flex-1 space-y-4">
                  {isEditing ? (
                    <form className="space-y-4">
                      <Input
                        label="Username"
                        value={formData.username}
                        onChange={(e) => handleInputChange('username', e.target.value)}
                        error={errors.username}
                        required
                        disabled
                        helperText="Gebruikersnaam kan niet worden gewijzigd"
                      />

                      <Input
                        label="E-mail"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        error={errors.email}
                        required
                        disabled
                        helperText="E-mailadres kan niet worden gewijzigd"
                      />

                      <Input
                        label="Leeftijd (optioneel)"
                        type="number"
                        min={16}
                        max={100}
                        value={formData.age || ''}
                        onChange={(e) => handleInputChange('age', e.target.value ? parseInt(e.target.value) : undefined)}
                        error={errors.age}
                      />
                    </form>
                  ) : (
                    <div className="space-y-1.5">
                      <h2 className="text-[26px] font-bold text-fg-bright">
                        {user?.username}
                      </h2>
                      <div className="flex items-center text-[15px] text-fg-muted">
                        <Mail className="h-4 w-4 mr-2 text-neon-cyan" />
                        <span>{user?.email}</span>
                      </div>
                      {user?.age && (
                        <div className="flex items-center text-[15px] text-fg-muted">
                          <Calendar className="h-4 w-4 mr-2 text-neon-cyan" />
                          <span>{user.age} jaar oud</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

            </div>

            {/* Over mij Section */}
            <div className="sf-divider mb-6"></div>
            <div>
              <h3 className="text-lg font-bold text-fg-bright mb-3">Over mij</h3>
            {/* Profile preferences */}
            {isEditing ? (
              <div className="space-y-4">
                {/* Bio */}
                <div>
                  <label className="mb-2 block text-sm font-semibold text-fg-soft">
                    Bio (optioneel)
                  </label>
                  <textarea
                    value={formData.bio || ''}
                    onChange={(e) => handleInputChange('bio', e.target.value)}
                    rows={4}
                    maxLength={500}
                    placeholder="Vertel anderen over jezelf, je festivalervaring, wat je zoekt..."
                    className={`sf-input min-h-[110px] resize-y ${
                      errors.bio ? '!border-neon-pink/60' : ''
                    }`}
                  />
                  <div className="mt-1.5 flex items-center justify-between">
                    {errors.bio && (
                      <p className="text-sm text-neon-pink-soft">{errors.bio}</p>
                    )}
                    <p className="ml-auto font-mono text-xs text-fg-meta">
                      {(formData.bio || '').length}/500
                    </p>
                  </div>
                </div>

                {/* Gender */}
                <div>
                  <label className="mb-2 block text-sm font-semibold text-fg-soft">
                    Geslacht (optioneel)
                  </label>
                  <select
                    value={formData.gender || ''}
                    onChange={(e) => handleInputChange('gender', e.target.value)}
                    className="sf-input appearance-none"
                  >
                    <option value="">Selecteer geslacht</option>
                    {genderOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Social Preference */}
                <div>
                  <label className="mb-2 block text-sm font-semibold text-fg-soft">
                    Sociale voorkeur (optioneel)
                  </label>
                  <select
                    value={formData.socialPreference || ''}
                    onChange={(e) => handleInputChange('socialPreference', e.target.value)}
                    className="sf-input appearance-none"
                  >
                    <option value="">Selecteer voorkeur</option>
                    {socialPreferenceOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Drug Use Preference */}
                <div>
                  <label className="mb-3 block text-sm font-semibold text-fg-soft">
                    Hoe sta jij op een feestje?
                  </label>
                  <div className="space-y-2">
                    {drugUsePreferenceOptions.map((option) => (
                      <label key={option.value} className="flex items-center cursor-pointer py-1">
                        <input
                          type="radio"
                          name="drugUsePreference"
                          value={option.value}
                          checked={formData.drugUsePreference === option.value}
                          onChange={(e) => handleInputChange('drugUsePreference', e.target.value)}
                          className="h-4 w-4 accent-neon-pink"
                        />
                        <span className="ml-2.5 text-sm text-fg-body select-none">{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {user?.bio ? (
                  <p className="text-[15px] text-fg-body">{user.bio}</p>
                ) : (
                  <p className="text-[15px] italic text-fg-meta">Nog geen bio toegevoegd</p>
                )}

                <div className="grid grid-cols-1 gap-x-10 gap-y-5 sm:grid-cols-2">
                  <div>
                    <div className="sf-label-mono mb-2">Geslacht</div>
                    <p className={user?.gender ? 'text-[15px] font-semibold text-fg-bright' : 'text-[15px] text-fg-meta'}>
                      {user?.gender ? formatGender(user.gender) : 'Niet opgegeven'}
                    </p>
                  </div>

                  <div>
                    <div className="sf-label-mono mb-2">Sociale voorkeur</div>
                    <p className={user?.socialPreference ? 'text-[15px] font-semibold text-fg-bright' : 'text-[15px] text-fg-meta'}>
                      {formatSocialPreference(user?.socialPreference || '')}
                    </p>
                  </div>

                  <div>
                    <div className="sf-label-mono mb-2">Hoe sta jij op een feestje?</div>
                    <p className={user?.drugUsePreference ? 'text-[15px] font-semibold text-fg-bright' : 'text-[15px] text-fg-meta'}>
                      {formatDrugUsePreference(user?.drugUsePreference || '')}
                    </p>
                  </div>
                </div>
              </div>
            )}
            </div>

            {/* Save button at bottom when editing - especially useful for mobile */}
            {isEditing && (
              <div className="mt-6 flex flex-col gap-3 border-t border-white/[.08] pt-6 sm:flex-row">
                <Button
                  type="button"
                  onClick={handleSubmit}
                  loading={isLoading}
                  size="md"
                  className="flex-1 sm:flex-initial"
                >
                  <Save className="h-4 w-4 mr-2" />
                  Wijzigingen opslaan
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleEditToggle}
                  disabled={isLoading}
                  size="md"
                  className="flex-1 sm:flex-initial"
                >
                  <X className="h-4 w-4 mr-2" />
                  Annuleren
                </Button>
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="sf-card p-5 sm:p-7">
            <h2 className="mb-5 text-xl font-bold text-fg-bright">Account instellingen</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <button
                onClick={() => window.location.href = '/account-upgrades'}
                className="flex items-center justify-center sm:justify-start gap-2.5 px-4 py-3.5 text-sm font-semibold text-fg-card bg-white/[.04] border border-white/10 rounded-field hover:bg-white/[.08] transition-colors"
              >
                <Zap className="h-4 w-4 text-neon-lime" />
                Upgrades
              </button>

              <button
                onClick={() => setIsChangePasswordModalOpen(true)}
                className="flex items-center justify-center sm:justify-start gap-2.5 px-4 py-3.5 text-sm font-semibold text-fg-card bg-white/[.04] border border-white/10 rounded-field hover:bg-white/[.08] transition-colors"
              >
                <Lock className="h-4 w-4 text-neon-cyan" />
                Wachtwoord wijzigen
              </button>

              <button
                onClick={handleLogout}
                className="flex items-center justify-center sm:justify-start gap-2.5 px-4 py-3.5 text-sm font-semibold text-neon-pink-soft bg-neon-pink/[.08] border border-neon-pink/30 rounded-field hover:bg-neon-pink/[.14] transition-colors"
              >
                <LogOut className="h-4 w-4" />
                Uitloggen
              </button>
            </div>
          </div>
      </div>

      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={isChangePasswordModalOpen}
        onClose={() => setIsChangePasswordModalOpen(false)}
      />
    </div>
  );
};

export default ProfilePage;
